import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faSearch, faTrash, faSpinner } from '@fortawesome/free-solid-svg-icons';
import apiService from '../services/apiService';
import { useToast } from '../contexts/ToastContext';
import { useConfirm } from '../contexts/ConfirmContext';
import './styles/CustomizedVariable.css';

/**
 * Delete IDs modal.
 *
 * The user supplies an identifier variable name (a table_single column) and a
 * list of its values. The backend resolves the matching internal ids, removes
 * those respondents from table_single / table_grid_single / table_grid_multiple
 * AND from the SPSS file, and archives the removed rows in deleted_* tables so
 * they can be restored later (from Project Details → Saved calculations).
 */
const DeleteIdsModal = ({ isOpen, onClose, projectId, user }) => {
  const { toast } = useToast();
  const { confirm } = useConfirm();

  const [varName, setVarName] = useState('');
  const [idsText, setIdsText] = useState('');
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [deleting, setDeleting] = useState(false);

  if (!isOpen) return null;

  const parseIds = () =>
    idsText.split(/[\s,;]+/).map((s) => s.trim()).filter(Boolean);

  const resetPreview = () => setPreview(null);

  const handlePreview = async () => {
    const values = parseIds();
    if (!varName.trim()) { toast.warning('Please enter the ID variable name'); return; }
    if (values.length === 0) { toast.warning('Please paste at least one ID value'); return; }
    setPreviewing(true);
    try {
      const { data } = await apiService.post('/delete-ids-preview', {
        projectId,
        varName: varName.trim(),
        values,
        user,
      }, { timeout: 300000 });
      if (!data?.success) throw new Error(data?.error || 'Preview failed');
      setPreview(data);
      if (data.matchedSpss === 0) {
        toast.warning('No respondents match the supplied IDs');
      }
    } catch (error) {
      setPreview(null);
      toast.error(error.message || 'Preview failed');
    } finally {
      setPreviewing(false);
    }
  };

  const handleDelete = async () => {
    if (!preview) return;
    const ok = await confirm({
      title: 'Delete respondents',
      message: `This will permanently remove ${preview.matchedSpss} respondent(s) matched on "${preview.variable}" from the SPSS file and all database tables. The removed rows are archived and can be restored later from Project Details. Proceed?`,
      confirmText: 'Delete',
      variant: 'danger',
    });
    if (!ok) return;
    setDeleting(true);
    try {
      const { data } = await apiService.post('/delete-ids', {
        projectId,
        varName: varName.trim(),
        values: parseIds(),
        user,
      }, { timeout: 600000 }); // SPSS rewrite + OneDrive upload can be slow
      if (!data?.success) throw new Error(data?.error || 'Delete failed');
      toast.success(
        `Removed ${data.removedSpss} respondent(s). The deletion was saved and the rows were archived (batch ${String(data.batchId).slice(0, 8)}…).`
      );
      setVarName('');
      setIdsText('');
      setPreview(null);
      onClose();
    } catch (error) {
      toast.error(error.message || 'Delete failed');
    } finally {
      setDeleting(false);
    }
  };

  const idsCount = parseIds().length;

  return (
    <div className="modal-overlay modal-overlay--above-settings" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 560 }}>

        <div className="modal-header">
          <h3>Delete IDs</h3>
          <button onClick={onClose} className="modal-close-button">
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        <div className="variable-name-input">
          <label>ID Variable Name:</label>
          <input
            type="text"
            value={varName}
            onChange={(e) => { setVarName(e.target.value); resetPreview(); }}
            placeholder="e.g. record / respid — the variable holding respondent IDs"
          />
        </div>

        <div className="variable-name-input" style={{ alignItems: 'flex-start' }}>
          <label>IDs to delete:</label>
          <textarea
            value={idsText}
            onChange={(e) => { setIdsText(e.target.value); resetPreview(); }}
            placeholder={'Paste IDs separated by commas, spaces or new lines\ne.g.\n1001\n1002\n1003'}
            rows={7}
            style={{ width: '100%', resize: 'vertical', fontFamily: 'monospace', fontSize: 13 }}
          />
        </div>
        <div style={{ fontSize: 12, color: '#6b7280', margin: '4px 0 10px' }}>
          {idsCount} ID value(s) entered
        </div>

        {preview && (
          <div style={{
            border: '1px solid #e5e7eb', borderRadius: 8, padding: '10px 12px',
            fontSize: 13, marginBottom: 10, background: '#f9fafb',
          }}>
            <div><strong>{preview.matchedSpss}</strong> respondent(s) will be removed (matched on "{preview.variable}").</div>
            {preview.matchedDb !== null && preview.matchedDb !== undefined && (
              <div style={{ color: '#6b7280' }}>{preview.matchedDb} matching row(s) found in the database.</div>
            )}
            {preview.missingValues?.length > 0 && (
              <div style={{ color: '#b45309', marginTop: 4 }}>
                {preview.missingValues.length} ID(s) not found:{' '}
                {preview.missingValues.slice(0, 15).join(', ')}
                {preview.missingValues.length > 15 ? '…' : ''}
              </div>
            )}
          </div>
        )}

        <div className="modal-footer">
          <div className="footer-buttons">
            <button
              className="add-row-btn"
              onClick={handlePreview}
              disabled={previewing || deleting}
            >
              {previewing
                ? <><FontAwesomeIcon icon={faSpinner} spin /> Checking…</>
                : <><FontAwesomeIcon icon={faSearch} /> Preview matches</>}
            </button>
            <button
              className="save-btn"
              onClick={handleDelete}
              disabled={!preview || preview.matchedSpss === 0 || deleting || previewing}
              title={!preview ? 'Run "Preview matches" first' : ''}
              style={{ background: (!preview || preview.matchedSpss === 0) ? undefined : '#dc2626' }}
            >
              {deleting
                ? <><FontAwesomeIcon icon={faSpinner} spin /> Deleting…</>
                : <><FontAwesomeIcon icon={faTrash} /> Delete {preview?.matchedSpss || ''} respondent(s)</>}
            </button>
          </div>
        </div>

      </div>
    </div>
  );
};

export default DeleteIdsModal;
